import { useEffect, useRef, useState, type ClipboardEvent, type FormEvent, type KeyboardEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@repletoryreach/sdk-auth';
import { Stack, Button, Group, Text } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { TextInput } from '@repletoryreach/sdk-ui/inputs';
import { useT } from '../../../../i18n/I18nProvider';
import { ArrowLeftIcon, DotIcon } from '../../components/icons';

const CODE_LENGTH = 6;

/**
 * Identity holds the pending token for five minutes from the moment the
 * password was accepted (`LoginHandler.cs`), and the login form navigates
 * here immediately, so counting from mount is within a second of the truth.
 */
const CHALLENGE_SECONDS = 300;

function mmss(total: number): string {
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * The TOTP challenge, on its own route.
 *
 * It used to live in `LoginForm`'s local state, which meant the challenge was
 * reachable only by failing a password prompt: a session that expires mid-flow,
 * or any later step-up authentication, had nowhere to send the operator. The
 * pending token is held by `AuthProvider` for five minutes, so this screen only
 * completes a challenge that this browser started; `onExpired` is what a cold
 * landing gets.
 */
export function MfaForm({ redirectTo }: { redirectTo: string }) {
  const t = useT();
  const { completeTwoFactor } = useAuth();
  const navigate = useNavigate();
  const [digits, setDigits] = useState<string[]>(() => Array<string>(CODE_LENGTH).fill(''));
  const [recovery, setRecovery] = useState('');
  // A recovery code is not six digits, so the boxed input cannot take one. The
  // field the hint has always promised is a mode of this screen rather than a
  // separate route.
  const [useRecovery, setUseRecovery] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [remaining, setRemaining] = useState(CHALLENGE_SECONDS);
  const boxes = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    if (remaining <= 0) return;
    const timer = window.setTimeout(() => setRemaining((s) => s - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [remaining]);

  const expired = remaining <= 0;
  const code = useRecovery ? recovery.trim() : digits.join('');
  const complete = useRecovery ? code.length > 0 : code.length === CODE_LENGTH;

  function setDigit(index: number, value: string) {
    const cleaned = value.replace(/\D/g, '');
    setDigits((prev) => {
      const next = [...prev];
      if (cleaned === '') {
        next[index] = '';
        return next;
      }
      // Typing over a filled box, or pasting into one, fills forwards.
      for (let i = 0; i < cleaned.length && index + i < CODE_LENGTH; i++) {
        next[index + i] = cleaned[i];
      }
      return next;
    });
    if (cleaned !== '') {
      const target = Math.min(index + cleaned.length, CODE_LENGTH - 1);
      boxes.current[target]?.focus();
    }
  }

  function handleKeyDown(index: number, e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace' && digits[index] === '' && index > 0) {
      e.preventDefault();
      setDigits((prev) => {
        const next = [...prev];
        next[index - 1] = '';
        return next;
      });
      boxes.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowLeft' && index > 0) boxes.current[index - 1]?.focus();
    if (e.key === 'ArrowRight' && index < CODE_LENGTH - 1) boxes.current[index + 1]?.focus();
  }

  function handlePaste(index: number, e: ClipboardEvent<HTMLInputElement>) {
    const text = e.clipboardData.getData('text');
    if (!/\d/.test(text)) return;
    e.preventDefault();
    setDigit(index, text);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (expired) return;
    setError(null);
    setBusy(true);
    try {
      await completeTwoFactor(code);
      navigate(redirectTo, { replace: true });
    } catch {
      setError(t('auth.login.twoFactor.mismatch'));
    } finally {
      setBusy(false);
    }
  }

  if (expired) {
    return (
      <Stack gap="md" align="stretch">
        <Alert color="warning" title={t('auth.mfa.expiredTitle')}>
          <Text fz="sm">{t('auth.mfa.expiredBody')}</Text>
        </Alert>
        <Link to="/auth/login" className="admin-auth__row-link" replace>
          <ArrowLeftIcon />
          {t('auth.login.twoFactor.back')}
        </Link>
      </Stack>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      <Stack gap="md">
        <Text fz="sm" ta="center">{t('auth.mfa.description')}</Text>

        <Group gap="xs" justify="center" align="center">
          <Text fz="sm" c="var(--rr-color-text-muted)">{t('auth.mfa.expiresIn')}</Text>
          <span className="admin-auth__countdown">{mmss(remaining)}</span>
        </Group>

        {useRecovery ? (
          <TextInput
            label={t('auth.mfa.recoveryLabel')}
            description={t('auth.mfa.recoveryDescription')}
            value={recovery}
            onChange={(e) => setRecovery(e.currentTarget.value)}
            required
            autoFocus
            autoComplete="one-time-code"
          />
        ) : (
          <fieldset className="admin-auth__code">
            <legend className="admin-auth__code-legend">{t('auth.login.twoFactor.code')}</legend>
            {digits.map((digit, i) => (
              <input
                key={i}
                ref={(node) => { boxes.current[i] = node; }}
                className="admin-auth__code-box"
                value={digit}
                onChange={(e) => setDigit(i, e.currentTarget.value)}
                onKeyDown={(e) => handleKeyDown(i, e)}
                onPaste={(e) => handlePaste(i, e)}
                onFocus={(e) => e.currentTarget.select()}
                inputMode="numeric"
                autoComplete={i === 0 ? 'one-time-code' : 'off'}
                aria-label={t('auth.mfa.digitLabel', { position: i + 1, total: CODE_LENGTH })}
                maxLength={CODE_LENGTH}
                autoFocus={i === 0}
              />
            ))}
          </fieldset>
        )}

        <div role="alert" aria-live="assertive">
          {error && <Alert color="error">{error}</Alert>}
        </div>

        <Button type="submit" loading={busy} disabled={!complete} fullWidth size="md">
          {t('action.continue')}
        </Button>

        <Stack gap={0} className="admin-auth__row-links">
          {!useRecovery && (
            <button type="button" className="admin-auth__row-link" onClick={() => setUseRecovery(true)}>
              <DotIcon />
              {t('auth.mfa.useRecovery')}
            </button>
          )}
          {useRecovery && (
            <button type="button" className="admin-auth__row-link" onClick={() => setUseRecovery(false)}>
              <DotIcon />
              {t('auth.mfa.useAuthenticator')}
            </button>
          )}
          <Link to="/auth/login" className="admin-auth__row-link" replace>
            <ArrowLeftIcon />
            {t('auth.login.twoFactor.back')}
          </Link>
        </Stack>
      </Stack>
    </form>
  );
}
